// ContentStore (ARCHITECTURE 6.1, 5.2): цепочка доверия, release_seq, загрузка и проверка файлов,
// атомарная смена релиза, сборка мусора, стоп-лист. Ни один байт контента не показывается без проверки.
import type { CoursePackage, GuidePackage } from "@brovka/content-types";
import type { Api } from "../api.ts";
import { b64ToBytes, sha256Hex, verifyEd25519 } from "../crypto.ts";
import type { BrovkaDB } from "../db.ts";

export type TrustedKey = { id: string; purpose: "release" | "killswitch"; alg: string; public_key: string };
export type TrustedKeys = { version: number; issued_at: string; keys: TrustedKey[] };

export type ManifestItem = {
  id: string;
  type: "course" | "guide";
  version: number;
  title: string;
  blob: string;
  size: number;
  stages: string[];
  access: string;
  min_client_schema: string;
  audio: string[];
  contents: string[];
};

export type Manifest = {
  release_id: string;
  release_seq: number;
  created_at: string;
  engine_version: string;
  schema_version: string;
  signing_key_id: string;
  items: ManifestItem[];
  dictionaries_blob: string;
  dev?: boolean;
};

export type KillswitchDoc = { seq: number; issued_at: string; min_release_seq: number; items: { content_id: string; reason: string }[] };
export type SignedKillswitch = { document: KillswitchDoc; signature: string; signing_key_id: string };

export type ClientDictionaries = {
  formats: Record<string, { code: string; name: string; refLengthM: number; refWidthM: number; outfieldPlayers: number; goalkeeper: boolean }>;
  stages: Record<string, { name: string; ages: [number, number]; kidMaxWords: number; defaultFormat: string; ttsMaxSec: number }>;
  principles: Record<string, { name: string; kid: string; parent: string; phase: string; minStage: string; requires: string[] }>;
  uiPhrases: Record<string, string>;
  uiAudio: Record<string, string>;
};

export type LoadedContent = {
  releaseId: string;
  releaseSeq: number;
  dev: boolean;
  dictionaries: ClientDictionaries;
  courses: CoursePackage[];
  guide: GuidePackage | null;
  hidden: Set<string>;
};

/** Хранилище файлов релиза (в браузере — Cache Storage, в тестах — память). */
export interface BlobCache {
  get(path: string): Promise<Uint8Array | undefined>;
  put(path: string, bytes: Uint8Array, contentType: string): Promise<void>;
  keys(): Promise<string[]>;
  delete(path: string): Promise<void>;
}

export class CacheStorageBlobs implements BlobCache {
  constructor(private readonly name = "brovka-content") {}
  private async cache() {
    return caches.open(this.name);
  }
  async get(path: string) {
    const r = await (await this.cache()).match(path);
    return r ? new Uint8Array(await r.arrayBuffer()) : undefined;
  }
  async put(path: string, bytes: Uint8Array, contentType: string) {
    await (await this.cache()).put(path, new Response(bytes as BodyInit, { headers: { "Content-Type": contentType, "Cache-Control": "public, max-age=31536000, immutable" } }));
  }
  async keys() {
    return (await (await this.cache()).keys()).map((r) => new URL(r.url).pathname);
  }
  async delete(path: string) {
    await (await this.cache()).delete(path);
  }
}

export class MemoryBlobs implements BlobCache {
  readonly map = new Map<string, Uint8Array>();
  async get(p: string) {
    return this.map.get(p);
  }
  async put(p: string, b: Uint8Array) {
    this.map.set(p, b);
  }
  async keys() {
    return [...this.map.keys()];
  }
  async delete(p: string) {
    this.map.delete(p);
  }
}

export class TrustError extends Error {}

type Current = {
  release_id: string;
  release_seq: number;
  manifest_url: string;
  signature_url: string;
  trusted_keys_url: string;
  trusted_keys_signature_url: string;
  killswitch_url: string;
};

type Applied = { releaseId: string; releaseSeq: number; manifest: Manifest };

const enc = new TextEncoder();
const dec = new TextDecoder();

/** Путь файла по ссылке `sha256:<hex>`. */
export function blobPath(ref: string, audio = false): string {
  return `/content/blobs/${ref.replace(/^sha256:/, "")}.${audio ? "m4a" : "json"}`;
}

export type Progress = (done: number, total: number) => void;

export class ContentStore {
  constructor(
    private readonly api: Api,
    private readonly db: BrovkaDB,
    private readonly blobs: BlobCache,
    private readonly rootKeyB64: string,
  ) {}

  private async trustedKeys(cur: Current): Promise<TrustedKeys> {
    if (!this.rootKeyB64) throw new TrustError("в сборке нет корневого ключа доверия");
    const [bytes, sig] = await Promise.all([this.api.bytes(cur.trusted_keys_url), this.api.bytes(cur.trusted_keys_signature_url)]);
    if (!(await verifyEd25519(sig, bytes, b64ToBytes(this.rootKeyB64)))) throw new TrustError("список доверенных ключей не подписан корнем");
    const tk = JSON.parse(dec.decode(bytes)) as TrustedKeys;
    const known = await this.db.get<TrustedKeys>("trustedKeys");
    if (known && tk.version < known.version) throw new TrustError(`список ключей версии ${tk.version} старее применённого ${known.version}`);
    await this.db.set("trustedKeys", tk);
    return tk;
  }

  private key(tk: TrustedKeys, id: string, purpose: TrustedKey["purpose"]): Uint8Array {
    const k = tk.keys.find((x) => x.id === id && x.purpose === purpose && x.alg === "ed25519");
    if (!k) throw new TrustError(`ключ ${id} не доверен для ${purpose}`);
    return b64ToBytes(k.public_key);
  }

  /** Проверить и применить стоп-лист (более новый по seq). */
  private async refreshKillswitch(tk: TrustedKeys): Promise<KillswitchDoc | undefined> {
    const s = await this.api.request<SignedKillswitch>("GET", "/v1/content/killswitch", undefined, { auth: false });
    const ok = await verifyEd25519(b64ToBytes(s.signature), enc.encode(JSON.stringify(s.document)), this.key(tk, s.signing_key_id, "killswitch"));
    if (!ok) throw new TrustError("подпись стоп-листа не сходится");
    const known = await this.db.get<KillswitchDoc>("killswitch");
    if (!known || s.document.seq > known.seq) {
      await this.db.set("killswitch", s.document);
      return s.document;
    }
    return known;
  }

  /**
   * Обновить контент с сервера (при запуске и выходе в сеть, ARCHITECTURE 6.4).
   * Возвращает true, если сменился релиз или стоп-лист.
   */
  async refresh(onProgress?: Progress): Promise<boolean> {
    const cur = await this.api.request<Current>("GET", "/v1/content/current", undefined, { auth: false });
    const tk = await this.trustedKeys(cur);
    const ksBefore = (await this.db.get<KillswitchDoc>("killswitch"))?.seq;
    const ks = await this.refreshKillswitch(tk);
    const applied = await this.db.get<Applied>("release");
    if (applied && applied.releaseId === cur.release_id && applied.releaseSeq === cur.release_seq) {
      await this.download(applied.manifest, onProgress);
      return ks?.seq !== ksBefore;
    }
    const [mBytes, mSig] = await Promise.all([this.api.bytes(cur.manifest_url), this.api.bytes(cur.signature_url)]);
    const manifest = JSON.parse(dec.decode(mBytes)) as Manifest;
    if (!(await verifyEd25519(mSig, mBytes, this.key(tk, manifest.signing_key_id, "release")))) throw new TrustError("подпись манифеста не сходится");
    if (manifest.release_id !== cur.release_id || manifest.release_seq !== cur.release_seq) throw new TrustError("манифест не соответствует текущему релизу");
    // Защита от возврата старых релизов: откат — только по стоп-листу с min_release_seq.
    if (applied && manifest.release_seq <= applied.releaseSeq) {
      const min = ks?.min_release_seq ?? 0;
      if (!(min > 0 && manifest.release_seq >= min)) throw new TrustError(`релиз ${manifest.release_seq} старее применённого ${applied.releaseSeq}`);
    }
    await this.download(manifest, onProgress);
    // Атомарная смена: релиз применяется только когда все файлы проверены и лежат в кэше.
    await this.db.set("release", { releaseId: manifest.release_id, releaseSeq: manifest.release_seq, manifest } satisfies Applied);
    await this.gc(manifest);
    return true;
  }

  private refs(m: Manifest): { path: string; ref: string; audio: boolean }[] {
    const out = new Map<string, { path: string; ref: string; audio: boolean }>();
    const add = (ref: string, audio: boolean) => out.set(blobPath(ref, audio), { path: blobPath(ref, audio), ref, audio });
    add(m.dictionaries_blob, false);
    for (const i of m.items) {
      add(i.blob, false);
      for (const a of i.audio) add(a, true);
    }
    return [...out.values()];
  }

  /** Загрузить недостающие файлы, проверив хэш каждого. */
  private async download(m: Manifest, onProgress?: Progress): Promise<void> {
    const refs = this.refs(m);
    let done = 0;
    const queue = [...refs];
    const worker = async () => {
      for (let r = queue.shift(); r; r = queue.shift()) {
        if (!(await this.blobs.get(r.path))) {
          const bytes = await this.api.bytes(r.path);
          if ((await sha256Hex(bytes)) !== r.ref.replace(/^sha256:/, "")) throw new TrustError(`файл ${r.path}: хэш не совпадает`);
          await this.blobs.put(r.path, bytes, r.audio ? "audio/mp4" : "application/json");
        }
        onProgress?.(++done, refs.length);
      }
    };
    await Promise.all([worker(), worker(), worker(), worker()]);
  }

  private async gc(m: Manifest): Promise<void> {
    const keep = new Set(this.refs(m).map((r) => r.path));
    for (const k of await this.blobs.keys()) if (k.startsWith("/content/blobs/") && !keep.has(k)) await this.blobs.delete(k);
  }

  private async json<T>(ref: string): Promise<T> {
    const path = blobPath(ref);
    let bytes = await this.blobs.get(path);
    if (!bytes) {
      bytes = await this.api.bytes(path);
      if ((await sha256Hex(bytes)) !== ref.replace(/^sha256:/, "")) throw new TrustError(`файл ${path}: хэш не совпадает`);
      await this.blobs.put(path, bytes, "application/json");
    }
    return JSON.parse(dec.decode(bytes)) as T;
  }

  /** Загрузить применённый релиз (работает офлайн). */
  async load(): Promise<LoadedContent | null> {
    const applied = await this.db.get<Applied>("release");
    if (!applied) return null;
    const m = applied.manifest;
    const dictionaries = await this.json<ClientDictionaries>(m.dictionaries_blob);
    const courses: CoursePackage[] = [];
    let guide: GuidePackage | null = null;
    for (const i of m.items) {
      if (i.type === "course") courses.push(await this.json<CoursePackage>(i.blob));
      else if (i.type === "guide") guide = await this.json<GuidePackage>(i.blob);
    }
    const ks = await this.db.get<KillswitchDoc>("killswitch");
    return { releaseId: m.release_id, releaseSeq: m.release_seq, dev: Boolean(m.dev), dictionaries, courses, guide, hidden: new Set(ks?.items.map((x) => x.content_id) ?? []) };
  }

  /** URL озвучки (Cache Storage отдаёт его офлайн через Service Worker). */
  static audioUrl(ref: string | undefined): string | undefined {
    return ref ? blobPath(ref, true) : undefined;
  }
}
